import { createRef, useCallback, useEffect, useRef, useState } from 'react';

export function useDataTags<
	BlockElement extends HTMLElement | null = null,
	LabelElement extends HTMLElement | null = null,
	ContentElement extends HTMLElement | null = null,
>(type: string, id?: string | number | null, values?: Record<string, unknown>) {
	const blockRef = useRef<BlockElement | null>(null);
	const labelRef = useRef<LabelElement | null>(null);
	const contentRef = useRef<ContentElement | null>(null);

	useEffect(() => {
		setBlockAttributes(blockRef.current, type, id, values);
		setLabelAttributes(labelRef.current);
		setContentAttributes(contentRef.current);
	});

	return {
		blockRef,
		labelRef,
		contentRef,
	};
}

export function useDataTagsFactory<
	BlockElement extends HTMLElement | null = null,
	LabelElement extends HTMLElement | null = null,
	ContentElement extends HTMLElement | null = null,
>() {
	const dataTagsRefsMap = useRef<
		Map<
			string | number,
			{
				blockRef: React.RefObject<BlockElement | null>;
				labelRef: React.RefObject<LabelElement | null>;
				contentRef: React.RefObject<ContentElement | null>;
			}
		>
	>(new Map());
	const [attributesRegistry] = useState(
		new Map<string | number, { type: string; id: string | number; values?: Record<string, unknown> }>(),
	);

	useEffect(() => {
		attributesRegistry.forEach(({ type, id, values }, key) => {
			const refs = dataTagsRefsMap.current.get(key);
			if (!refs) return;

			setBlockAttributes(refs.blockRef.current, type, id, values);
			setLabelAttributes(refs.labelRef.current);
			setContentAttributes(refs.contentRef.current);
		});
	});

	const createDataTags = useCallback(
		(type: string, id: string | number, values?: Record<string, unknown>) => {
			if (!dataTagsRefsMap.current.has(id)) {
				dataTagsRefsMap.current.set(id, {
					blockRef: createRef<BlockElement | null>(),
					labelRef: createRef<LabelElement | null>(),
					contentRef: createRef<ContentElement | null>(),
				});
			}

			attributesRegistry.set(id, { type, id, values });

			const refs = dataTagsRefsMap.current.get(id);
			if (!refs) {
				throw new Error(`Refs for ${type} with id ${id} should exist but were not found`);
			}

			return refs;
		},
		[attributesRegistry],
	);

	return { createDataTags };
}

function setBlockAttributes(
	el: HTMLElement | null,
	type: string,
	id?: string | number | null,
	values?: Record<string, unknown>,
): void {
	if (!el) return;

	el.setAttribute('data-tag-block', '');
	el.setAttribute('data-tag-block-type', type);

	if (id) {
		el.setAttribute('data-tag-block-id', `${id}`);
	} else {
		el.removeAttribute('data-tag-block-id');
	}

	if (values) {
		el.setAttribute('data-tag-block-values', JSON.stringify(values));
	} else {
		el.removeAttribute('data-tag-block-values');
	}
}

function setLabelAttributes(el: HTMLElement | null): void {
	if (!el) return;

	el.setAttribute('data-tag-block-label', '');
}

function setContentAttributes(el: HTMLElement | null): void {
	if (!el) return;

	el.setAttribute('data-tag-block-content', '');
}
